import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Doughnut } from "react-chartjs-2";
import "./Indicator.css";

function Indicator({
  id,
  description,
  weight,
  level,
  status,
  lastCheckInLevel,
  lastCheckInCompleted,
  type,
  minRange,
  maxRange,
  completed,
  checkInsLength,
  lastCheckInDate,
  language,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dayjs = require("dayjs");

  const selectIndicatorLevelBadge = (
    level,
    minRange,
    maxRange,
    type,
    completed,
  ) => {
    if (type === "incremental") {
      if (level >= minRange && level <= maxRange) {
        return "green-badge";
      } else if (level < minRange) {
        return "red-badge";
      } else if (level > maxRange) {
        return "blue-badge";
      }
    } else if (type === "decremental") {
      if (level >= minRange && level <= maxRange) {
        return "green-badge";
      } else if (level < minRange) {
        return "blue-badge";
      } else if (level > maxRange) {
        return "red-badge";
      }
    } else if (type === "completive") {
      if (completed) {
        return "green-badge";
      } else {
        return "red-badge";
      }
    }
  };

  const selectDataByLevel = (
    status,
    level,
    minRange,
    maxRange,
    type,
    completedStatus,
  ) => {
    let data = {};
    let labels = ["Below", "In", "Over"];
    let datasets = [];
    if (status === "pending") {
      datasets = [
        {
          data: [33, 33, 34], // Value out of 100
          backgroundColor: ["#E0E0E0", "#E0E0E0", "#E0E0E0"],
          hoverBackgroundColor: ["#E0E0E0", "#E0E0E0", "#E0E0E0"],
          borderWidth: 0,
        },
      ];
    } else if (checkInsLength === 0) {
      datasets = [
        {
          data: [33, 33, 34], // Value out of 100
          backgroundColor: ["#E0E0E0", "#E0E0E0", "#E0E0E0"],
          hoverBackgroundColor: ["#E0E0E0", "#E0E0E0", "#E0E0E0"],
          borderWidth: 0,
        },
      ];
    } else {
      if (type === "incremental") {
        if (level >= minRange && level <= maxRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              hoverBackgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        } else if (level < minRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              hoverBackgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        } else if (level > maxRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#E0E0E0", "#E0E0E0", "#5d78ff"],
              hoverBackgroundColor: ["#E0E0E0", "#E0E0E0", "#5d78ff"],
              borderWidth: 0,
            },
          ];
        }
      } else if (type === "decremental") {
        if (level >= minRange && level <= maxRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              hoverBackgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        } else if (level < minRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#E0E0E0", "#E0E0E0", "#5d78ff"],
              hoverBackgroundColor: ["#E0E0E0", "#E0E0E0", "#5d78ff"],
              borderWidth: 0,
            },
          ];
        } else if (level > maxRange) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              hoverBackgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        }
      } else if (type === "completive") {
        if (completedStatus) {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              hoverBackgroundColor: ["#E0E0E0", "#1dc9b8", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        } else {
          datasets = [
            {
              data: [33, 33, 34], // Value out of 100
              backgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              hoverBackgroundColor: ["#fd397a", "#E0E0E0", "#E0E0E0"],
              borderWidth: 0,
            },
          ];
        }
      }
    }
    data = { labels, datasets };
    return data;
  };

  const selectClassByLevel = (
    status,
    level,
    minRange,
    maxRange,
    type,
    completed,
  ) => {
    if (status === "pending") {
      return "indicator-card-gray";
    } else {
      if (type === "incremental") {
        if (level >= minRange && level <= maxRange) {
          return "indicator-card-green";
        } else if (level < minRange) {
          return "indicator-card-red";
        } else if (level > maxRange) {
          return "indicator-card-blue";
        }
      } else if (type === "decremental") {
        if (level >= minRange && level <= maxRange) {
          return "indicator-card-green";
        } else if (level < minRange) {
          return "indicator-card-blue";
        } else if (level > maxRange) {
          return "indicator-card-red";
        }
      } else if (type === "completive") {
        if (completed) {
          return "indicator-card-green";
        } else {
          return "indicator-card-red";
        }
      }
    }
  };

  const options = {
    circumference: 180,
    rotation: 270, // Rotates the chart to place the open end at the bottom
    cutout: "50%", // Adjust thickness
    layout: {
      padding: 0,
      margin: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      // Custom plugins might be needed for text/needle
      datalabels: {
        display: false,
      },
    },
  };

  return (
    <Col key={id} lg={4}>
      <Col
        className={selectClassByLevel(
          status,
          lastCheckInLevel,
          minRange,
          maxRange,
          type,
          lastCheckInCompleted,
        )}
        onClick={() => navigate(`/indicators/details/${id}`)}
      >
        <Row className="align-items-center mb-2">
          <Col>
            <span className="weight">{weight}</span>
          </Col>
          <Col className="right">
            <span
              className={selectIndicatorLevelBadge(
                lastCheckInLevel,
                minRange,
                maxRange,
                type,
                lastCheckInCompleted,
              )}
            >
              {type === "completive"
                ? lastCheckInCompleted
                  ? language === "es"
                    ? "Completado"
                    : "Completed"
                  : language === "es"
                    ? "No Completado"
                    : "Not Completed"
                : lastCheckInLevel}
            </span>
          </Col>
        </Row>

        <Col className="center">
          <p className="lastCheckIn">
            {lastCheckInDate ? dayjs(lastCheckInDate).format("D-MMM-YYYY") : ""}
          </p>
        </Col>

        <Col className="center">
          <p className="description">{description}</p>
        </Col>

        <Row className="align-items-center">
          <Col>
            <Col className="range-container">
              <p className="range">
                {type === "completive" ? "N/A" : minRange}
              </p>
              <h6 className="rangeLabel">
                {t("Indicadores.Indicador.Minimo")}
              </h6>
            </Col>
          </Col>
          <Col className="right">
            <Col className="range-container">
              <p className="range">
                {type === "completive" ? "N/A" : maxRange}
              </p>
              <h6 className="rangeLabel">
                {t("Indicadores.Indicador.Maximo")}
              </h6>
            </Col>
          </Col>
        </Row>

        <Col className="gauge-container">
          <Doughnut
            data={
              level !== 0 || completed
                ? selectDataByLevel(
                    status,
                    level,
                    minRange,
                    maxRange,
                    type,
                    completed,
                  )
                : selectDataByLevel(
                    status,
                    lastCheckInLevel,
                    minRange,
                    maxRange,
                    type,
                    lastCheckInCompleted,
                  )
            }
            options={options}
          />
        </Col>
      </Col>
    </Col>
  );
}

export default Indicator;
